import React, { useState } from 'react';
import { ChevronLeft, Loader2 } from 'lucide-react';

export default function PersonalityQuestions({ questions, onSubmit, onBack }) {
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const question = questions[current];
  const total = questions.length;
  const progress = ((current + 1) / total) * 100;

  const handleSelect = (option) => {
    setSelected(option);
  };

  const handleNext = async () => {
    if (!selected) return;

    const answer = {
      question_id: question.id,
      answer_id: selected.id,
      points: selected.points,
    };

    const newAnswers = [...answers, answer];
    setAnswers(newAnswers);

    if (current < total - 1) {
      setCurrent(current + 1);
      setSelected(null);
    } else {
      setSubmitting(true);
      await onSubmit(newAnswers);
    }
  };

  const handleBack = () => {
    if (current > 0) {
      setCurrent(current - 1);
      setAnswers(answers.slice(0, -1));
      setSelected(null);
    } else {
      onBack();
    }
  };

  if (!question) return null;

  return (
    <div className="max-w-2xl mx-auto" data-testid="personality-questions">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <button onClick={handleBack}
          className="flex items-center gap-1 text-sm transition-all hover:opacity-70"
          style={{ color: 'var(--primary)' }} data-testid="questions-back">
          <ChevronLeft className="w-4 h-4" /> Back
        </button>
        <span className="text-xs font-medium" style={{ color: 'var(--muted-fg)', fontFamily: 'Manrope' }}>
          {current + 1} / {total}
        </span>
      </div>

      {/* Progress Bar */}
      <div className="h-2 rounded-full mb-8 overflow-hidden" style={{ background: 'var(--muted)' }}>
        <div className="h-full rounded-full transition-all duration-500 ease-out"
          style={{ width: `${progress}%`, background: 'var(--primary)' }} data-testid="question-progress" />
      </div>

      {/* Question */}
      <div className="animate-fade-in" key={current}>
        <h2 className="text-xl sm:text-2xl font-bold mb-8 leading-snug" style={{ fontFamily: 'Manrope', color: 'var(--text)' }} data-testid="question-text">
          {question.text}
        </h2>

        {/* Options */}
        <div className="space-y-3">
          {question.options.map((option, i) => {
            const isSelected = selected?.id === option.id;
            return (
              <button key={option.id} onClick={() => handleSelect(option)}
                className={`w-full p-4 rounded-lg border text-left transition-all hover:-translate-y-0.5 hover:shadow-sm animate-fade-in`}
                style={{
                  background: isSelected ? 'var(--primary)' : 'var(--surface)',
                  color: isSelected ? 'var(--primary-fg)' : 'var(--text)',
                  borderColor: isSelected ? 'var(--primary)' : 'var(--border)',
                  animationDelay: `${i * 0.05}s`,
                }}
                data-testid={`option-${option.id}`}>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full border-2 flex items-center justify-center shrink-0 text-xs font-bold"
                    style={{
                      borderColor: isSelected ? 'rgba(255,255,255,0.5)' : 'var(--border)',
                      background: isSelected ? 'rgba(255,255,255,0.2)' : 'transparent',
                      color: isSelected ? 'var(--primary-fg)' : 'var(--muted-fg)',
                    }}>
                    {String.fromCharCode(65 + i)}
                  </div>
                  <span className="text-sm font-medium">{option.text}</span>
                </div>
              </button>
            );
          })}
        </div>

        {/* Next Button */}
        <button onClick={handleNext} disabled={!selected || submitting} data-testid="question-next"
          className="mt-8 w-full py-3 rounded-lg font-semibold transition-all hover:opacity-90 active:scale-[0.98] disabled:opacity-40 flex items-center justify-center gap-2"
          style={{ background: 'var(--primary)', color: 'var(--primary-fg)' }}>
          {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
          {current < total - 1 ? 'Next Question' : 'See My Results'}
        </button>
      </div>
    </div>
  );
}
